import { createTheme } from '@mui/material/styles';
import tokens, { generateColor, generateOpacityColor } from '@verifime/design-tokens';
import {
  commonComponentsOverride,
  paletteGrey,
  semanticBorderRadius,
  semanticFontWeight,
  semanticFontSize,
  semanticLetterSpacing,
  semanticLineHeight,
  semanticSpacing,
  semanticSize,
  typographyTheme,
} from './common';

const errorContent = generateColor(tokens.brandColorError, '700');
const errorFill = generateColor(tokens.brandColorError, '050');
const warningContent = generateColor(tokens.brandColorWarning, '700');
const warningFill = generateColor(tokens.brandColorWarning, '050');
const infoContent = generateColor(tokens.brandColorInfo, '700');
const infoFill = generateColor(tokens.brandColorInfo, '050');
const successContent = generateColor(tokens.brandColorSuccess, '700');
const successFill = generateColor(tokens.brandColorSuccess, '050');

const theme = createTheme({
  semantic: {
    borderRadius: semanticBorderRadius,
    fontWeight: semanticFontWeight,
    fontSize: semanticFontSize,
    letterSpacing: semanticLetterSpacing,
    lineHeight: semanticLineHeight,
    spacing: semanticSpacing,
    size: semanticSize,
  },
  logo: {
    width: semanticSize['2xs'],
    padding: semanticSpacing['2xs'],
    borderRadius: semanticSpacing['2xs'],
  },
  card: {
    padding: semanticSpacing.base,
    borderRadius: semanticBorderRadius.sm,
    borderWidth: semanticSpacing['4xs'],
    contentGap: semanticSpacing.sm,
    itemGap: semanticSpacing.sm,
  },
  palette: {
    mode: 'light',
    primary: {
      main: generateColor(tokens.brandColorPrimary, '500'),
      dark: generateColor(tokens.brandColorPrimary, '600'),
      light: generateColor(tokens.brandColorPrimary, '400'),
      contrastText: tokens.colorWhite,
    },
    secondary: {
      main: generateColor(tokens.brandColorSecondary, '500'),
      dark: generateColor(tokens.brandColorSecondary, '600'),
      light: generateColor(tokens.brandColorSecondary, '400'),
      contrastText: tokens.colorWhite,
    },
    error: {
      main: generateColor(tokens.brandColorError, '500'),
      dark: generateColor(tokens.brandColorError, '600'),
      light: generateColor(tokens.brandColorError, '400'),
      contrastText: tokens.colorWhite,
    },
    warning: {
      main: generateColor(tokens.brandColorWarning, '500'),
      dark: generateColor(tokens.brandColorWarning, '600'),
      light: generateColor(tokens.brandColorWarning, '400'),
      contrastText: tokens.colorWhite,
    },
    info: {
      main: generateColor(tokens.brandColorInfo, '500'),
      dark: generateColor(tokens.brandColorInfo, '600'),
      light: generateColor(tokens.brandColorInfo, '400'),
      contrastText: tokens.colorWhite,
    },
    success: {
      main: generateColor(tokens.brandColorSuccess, '500'),
      dark: generateColor(tokens.brandColorSuccess, '600'),
      light: generateColor(tokens.brandColorSuccess, '400'),
      contrastText: tokens.colorWhite,
    },
    text: {
      primary: generateOpacityColor(tokens.colorBlack, 0.9),
      secondary: generateOpacityColor(tokens.colorBlack, 0.6),
      disabled: generateOpacityColor(tokens.colorBlack, 0.4),
      success: generateColor(tokens.brandColorSuccess, '500'),
      danger: generateColor(tokens.brandColorError, '500'),
    },
    background: {
      default: tokens.colorGrey020,
      paper: tokens.colorWhite,
    },
    action: {
      disabled: generateOpacityColor(tokens.colorBlack, 0.4),
      disabledBackground: generateOpacityColor(tokens.colorBlack, 0.1),
      disabledOpacity: 0.4,
      focus: generateOpacityColor(tokens.colorBlack, 0.1),
      focusOpacity: 0.1,
      selected: generateColor(tokens.brandColorPrimary, '200'),
      selectedOpacity: 0.2,
      hover: generateOpacityColor(tokens.colorBlack, 0.05),
      hoverOpacity: 0.05,
      active: generateOpacityColor(tokens.colorBlack, 0.5),
      activatedOpacity: 0.1,
    },
    surface: {
      logo: tokens.colorWhite,
      brandSubtle: generateColor(tokens.brandColorPrimary, '050'),
    },
    border: {
      decorative: tokens.colorGrey500,
    },
    status: {
      success: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorSuccess, '500'),
        icon: tokens.colorWhite,
      },
      warning: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorWarning, '600'),
        icon: tokens.colorWhite,
      },
      error: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorError, '500'),
        icon: tokens.colorWhite,
      },
      info: {
        text: generateColor(tokens.brandColorInfo, '600'),
        border: generateColor(tokens.brandColorInfo, '600'),
      },
    },
    interactive: {
      disabled: {
        text: tokens.colorWhite,
        fill: generateOpacityColor(tokens.colorBlack, 0.4),
        icon: tokens.colorWhite,
      },
      enabled: {
        text: generateOpacityColor(tokens.colorBlack, 0.5),
        fill: generateOpacityColor(tokens.colorBlack, 0.5),
        icon: generateOpacityColor(tokens.colorBlack, 0.25),
      },
    },
    risk: {
      extreme: {
        text: tokens.colorWhite,
        fill: tokens.colorGrey800,
        border: generateOpacityColor(tokens.colorBlack, 0.9),
        icon: tokens.colorWhite,
      },
      high: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorError, '500'),
        icon: tokens.colorWhite,
      },
      medium: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorWarning, '600'),
        icon: tokens.colorWhite,
      },
      low: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorSuccess, '500'),
        icon: tokens.colorWhite,
      },
      pending: {
        text: generateOpacityColor(tokens.colorBlack, 0.4),
        fill: tokens.colorWhite,
        border: generateOpacityColor(tokens.colorBlack, 0.4),
        icon: generateOpacityColor(tokens.colorWhite, 0.5),
      },
    },
    confidence: {
      none: {
        text: generateOpacityColor(tokens.colorBlack, 0.6),
        fill: tokens.colorGrey100,
      },
      pending: {
        text: generateOpacityColor(tokens.colorBlack, 0.6),
        fill: tokens.colorGrey200,
      },
      low: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorError, '500'),
      },
      substantial: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorWarning, '500'),
      },
      high: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorSuccess, '500'),
      },
      /**
       * The "what do you do now" box beneath a card's statement. Deliberately not one of the tone
       * colours above: `ConfidenceGuidance` renders on every axis outcome that carries a next
       * step, independent of which tone the meter above it is showing, so it takes its own pair
       * from the brand primary ramp rather than borrowing a tone that would tie its appearance to
       * a specific outcome.
       */
      guidance: {
        fill: generateColor(tokens.brandColorPrimary, '050'),
        text: generateColor(tokens.brandColorPrimary, '800'),
      },
    },
    // Identical across all four themes on purpose: these sit over a video frame, not over
    // the page surface, so they take their contrast from the capture rather than the theme.
    mediaOverlay: {
      scrim: generateOpacityColor(tokens.colorBlack, 0.72),
      control: generateOpacityColor(tokens.colorWhite, 0.22),
      controlHover: generateOpacityColor(tokens.colorWhite, 0.34),
      track: generateOpacityColor(tokens.colorWhite, 0.28),
      halo: generateOpacityColor(tokens.colorWhite, 0.34),
    },
    entity: {
      verified: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorSuccess, '500'),
        icon: tokens.colorWhite,
      },
      rejected: {
        text: tokens.colorWhite,
        fill: generateColor(tokens.brandColorError, '500'),
        icon: tokens.colorWhite,
      },
      draft: {
        text: tokens.colorWhite,
        fill: generateOpacityColor(tokens.colorBlack, 0.4),
        icon: generateOpacityColor(tokens.colorWhite, 0.5),
      },
      inProgress: {
        text: tokens.colorWhite,
        fill: generateOpacityColor(tokens.colorBlack, 0.4),
        icon: tokens.colorWhite,
      },
      empty: {
        text: tokens.colorWhite,
        fill: generateOpacityColor(tokens.colorBlack, 0.4),
        icon: tokens.colorWhite,
      },
    },
    grey: paletteGrey,
  },
  typography: typographyTheme,
  components: {
    MuiButton: {
      variants: [
        ...commonComponentsOverride.MuiButton!.variants!,
        {
          props: { variant: 'text', color: 'primary' },
          style: {
            color: generateColor(tokens.brandColorPrimary, '500'),
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            color: generateColor(tokens.brandColorPrimary, '500'),
          },
        },
      ],
    },
    MuiLink: {
      defaultProps: {
        color: generateColor(tokens.brandColorPrimary, '500'),
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          backgroundColor: errorFill,
          '& .MuiAlertTitle-root': {
            color: errorContent,
          },
          '& .MuiTypography-root': {
            color: errorContent,
          },
          '& button': {
            color: errorContent,
          },
        },
        standardWarning: {
          backgroundColor: warningFill,
          '& .MuiAlertTitle-root': {
            color: warningContent,
          },
          '& .MuiTypography-root': {
            color: warningContent,
          },
          '& button': {
            color: warningContent,
          },
        },
        standardInfo: {
          backgroundColor: infoFill,
          '& .MuiAlertTitle-root': {
            color: infoContent,
          },
          '& .MuiTypography-root': {
            color: infoContent,
          },
          '& button': {
            color: infoContent,
          },
        },
        standardSuccess: {
          backgroundColor: successFill,
          '& .MuiAlertTitle-root': {
            color: successContent,
          },
          '& .MuiTypography-root': {
            color: successContent,
          },
          '& button': {
            color: successContent,
          },
        },
      },
    },
  },
});

export default theme;
